import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Typography,
  CircularProgress
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import API_CONFIG from '../../config/api';

const ACCEPTED_EXT = /\.(png|jpe?g|gif|webp|bmp|pdf|doc|docx)$/i;

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function detectUploadType(file) {
  if (!file) return 'document';
  if (file.type?.startsWith('image/')) return 'photo';
  if (file.type?.startsWith('audio/')) return 'audio';
  return 'document';
}

function isAllowedFile(file) {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  if (file.type === 'application/pdf') return true;
  if (
    file.type === 'application/msword' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return true;
  }
  return ACCEPTED_EXT.test(file.name || '');
}

function fileFromClipboardItem(item) {
  const blob = item.getAsFile?.();
  if (!blob) return null;
  const ext = (blob.type || 'image/png').split('/')[1] || 'png';
  const name = blob.name && blob.name !== 'image.png' ? blob.name : `print-${Date.now()}.${ext}`;
  return new File([blob], name, { type: blob.type || 'image/png' });
}

/**
 * Composer — texto, anexo (clipe / Ctrl+V / drag-drop), nota de voz.
 * ref.attachFiles(FileList|File[]) — usado pelo ChatWindow na área da conversa.
 */
const Composer = forwardRef(function Composer(
  { conversationId, onSendMessage, onTypingStart, onTypingStop, disabled },
  ref
) {
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const dragDepthRef = useRef(0);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearTimer();
      stopStream();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const applySelectedFile = useCallback((file) => {
    if (!file) return false;
    if (!isAllowedFile(file)) {
      setError('Formato não suportado. Use imagem, PDF ou Word.');
      return false;
    }
    setError('');
    setAudioBlob(null);
    setSelectedFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
    });
    return true;
  }, []);

  const attachFiles = useCallback(
    (files) => {
      if (disabled || sending || isRecording) return false;
      const list = Array.from(files || []).filter(Boolean);
      if (!list.length) return false;
      // Um anexo por vez (mesmo fluxo do clipe)
      return applySelectedFile(list[0]);
    },
    [disabled, sending, isRecording, applySelectedFile]
  );

  useImperativeHandle(ref, () => ({ attachFiles }), [attachFiles]);

  const uploadFile = async (file, type) => {
    // FASE 4: POST /api/chat/upload (multer) → { url, name, size, type, mimeType }
    // Depois: onSendMessage(conversationId, { type, fileUrl: url, fileName, fileSize }) via socket
    const form = new FormData();
    // type ANTES do file — o multer lê body.type no fileFilter
    form.append('type', type);
    form.append('file', file);
    const token = localStorage.getItem('sessionToken');
    const res = await fetch(`${API_CONFIG.BACKEND_URL}/api/chat/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Erro no upload');
    return data;
  };

  const startRecording = useCallback(async () => {
    if (disabled || sending || isRecording) return;
    setError('');
    setAudioBlob(null);
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size > 0) setAudioBlob(blob);
        chunksRef.current = [];
      };

      mr.start();
      setIsRecording(true);
      setRecordingTime(0);
      clearTimer();
      timerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch (err) {
      console.error('[Composer] startRecording', err);
      setError('Não foi possível acessar o microfone.');
      stopStream();
    }
  }, [disabled, sending, isRecording]);

  const stopRecording = useCallback(() => {
    clearTimer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    clearTimer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        stopStream();
        chunksRef.current = [];
      };
      mediaRecorderRef.current.stop();
    } else {
      stopStream();
    }
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingTime(0);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) attachFiles([file]);
  };

  const clearAttachment = () => {
    setSelectedFile(null);
    setAudioBlob(null);
    setRecordingTime(0);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handlePaste = (e) => {
    if (disabled || sending || isRecording) return;
    const items = e.clipboardData?.items;
    if (!items?.length) return;

    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = fileFromClipboardItem(item);
        if (file) attachFiles([file]);
        return;
      }
    }

    // Alguns browsers expõem arquivos em clipboardData.files
    const files = e.clipboardData?.files;
    if (files?.length) {
      const img = Array.from(files).find((f) => f.type.startsWith('image/') || isAllowedFile(f));
      if (img) {
        e.preventDefault();
        attachFiles([img]);
      }
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || sending || isRecording) return;
    dragDepthRef.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (disabled || sending || isRecording) return;
    const files = e.dataTransfer?.files;
    if (files?.length) attachFiles(files);
  };

  const handleSend = async () => {
    if (disabled || sending || isRecording) return;
    const t = (text || '').trim();
    const hasMedia = !!(selectedFile || audioBlob);
    if (!t && !hasMedia) return;

    setSending(true);
    setError('');
    try {
      if (audioBlob) {
        const file = new File([audioBlob], `audio-${Date.now()}.webm`, {
          type: audioBlob.type || 'audio/webm'
        });
        const data = await uploadFile(file, 'audio');
        onSendMessage(
          conversationId,
          {
            type: 'audio',
            content: t || null,
            fileUrl: data.url,
            fileName: data.name,
            fileSize: data.size
          },
          () => {}
        );
      } else if (selectedFile) {
        const kind = detectUploadType(selectedFile);
        const data = await uploadFile(selectedFile, kind === 'photo' ? 'photo' : 'document');
        onSendMessage(
          conversationId,
          {
            type: kind === 'photo' ? 'photo' : 'file',
            content: t || null,
            fileUrl: data.url,
            fileName: data.name || selectedFile.name,
            fileSize: data.size ?? selectedFile.size
          },
          () => {}
        );
      } else if (t) {
        onSendMessage(conversationId, { type: 'text', content: t }, () => {});
      }

      setText('');
      clearAttachment();
      if (onTypingStop) onTypingStop(conversationId);
    } catch (err) {
      console.error('[Composer] handleSend', err);
      setError(err.message || 'Falha ao enviar.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (onTypingStart) {
      onTypingStart(conversationId);
    }
  };

  const canSend =
    !disabled && !sending && !isRecording && (!!(text || '').trim() || !!selectedFile || !!audioBlob);

  return (
    <Box
      onPaste={handlePaste}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        p: 1.5,
        borderTop: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        position: 'relative',
        transition: 'background-color 0.15s ease, border-color 0.15s ease'
      }}
    >
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            border: '2px dashed',
            borderColor: 'primary.main',
            pointerEvents: 'none'
          }}
        >
          <Typography variant="body2" fontWeight={600} color="primary.main">
            Solte para anexar
          </Typography>
        </Box>
      )}

      {(selectedFile || audioBlob) && !isRecording && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt="Pré-visualização"
              sx={{
                width: 56,
                height: 56,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
          )}
          {selectedFile && (
            <Chip
              icon={
                selectedFile.type?.startsWith('image/') ? (
                  <ImageIcon sx={{ fontSize: 18 }} />
                ) : (
                  <InsertDriveFileIcon sx={{ fontSize: 18 }} />
                )
              }
              label={selectedFile.name}
              onDelete={clearAttachment}
              size="small"
              variant="outlined"
              sx={{ maxWidth: '100%' }}
            />
          )}
          {audioBlob && (
            <Chip
              icon={<MicIcon sx={{ fontSize: 18 }} />}
              label={`Áudio ${formatTime(recordingTime || 1)}`}
              onDelete={clearAttachment}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.75 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
        {isRecording ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: 'error.50'
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'error.main',
                animation: 'composerRecPulse 1s ease-in-out infinite',
                '@keyframes composerRecPulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.45, transform: 'scale(1.25)' }
                }
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main', flex: 1 }}>
              Gravando… {formatTime(recordingTime)}
            </Typography>
            <Tooltip title="Cancelar">
              <IconButton size="small" onClick={cancelRecording} aria-label="Cancelar gravação">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Parar">
              <IconButton size="small" color="error" onClick={stopRecording} aria-label="Parar gravação">
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <TextField
            size="small"
            placeholder="Digite, cole (Ctrl+V) ou arraste um arquivo..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={() => onTypingStop && onTypingStop(conversationId)}
            multiline
            maxRows={4}
            disabled={disabled || sending}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.50',
                borderRadius: 2,
                fontSize: 13
              }
            }}
          />
        )}

        {!isRecording && (
          <>
            <Tooltip title="Anexar arquivo (PDF / imagem)">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || sending}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Anexar arquivo"
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Nota de voz">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || sending}
                  onClick={startRecording}
                  aria-label="Gravar áudio"
                  sx={{ color: 'text.secondary' }}
                >
                  <MicIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                minWidth: 88,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2
              }}
            >
              {sending ? <CircularProgress size={18} color="inherit" /> : 'Enviar'}
            </Button>
          </>
        )}
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/*,application/pdf,.pdf,.doc,.docx"
        onChange={handleFileSelect}
      />
    </Box>
  );
});

export default Composer;
